import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { LanguageProvider } from "./contexts/LanguageContext";
import { PageTransitionProvider } from "./components/PageTransition";

// Initialize i18n
import './i18n';
import { GucciPageLayout, GucciFooter } from "./components/GucciPageComponents";
import GucciSunsariHome, { TopBar, MainNavigation } from "./components/GucciSunsariHome";
import MaintenanceNotice from "./components/MaintenanceNotice";
import HorizontalSlideRestaurant from "./components/HorizontalSlideRestaurant";
import EnhancedMenuPage from "./components/EnhancedMenuPage";
import NordicLunchMenu from "./components/NordicLunchMenu";
import StunningReservationPage from "./components/StunningReservationPage";
import AboutPage from "./components/AboutPage";
import ContactPage from "./components/ContactPage";
import CateringPage from "./components/CateringPage";
import AdminPanel from "./components/admin/AdminPanel";
import AdminLogin from "./components/AdminLogin";
import OrderPage from "./components/OrderPage";
import OffersPage from "./components/OffersPage";
import InteractiveTableBooking from "./components/InteractiveTableBooking";
import CombineTableBooking from "./components/CombineTableBooking";
import GiftCardPage from "./components/GiftCardPage";
import OrderTracking from "./components/OrderTracking";
import OrderLookup from "./components/OrderLookup";
import CustomerPage from "./components/CustomerPage";
import SuggestDishPage from "./components/SuggestDishPage";
import PrintableLunchMenu from "./components/PrintableLunchMenu";
import MobileLunchBar from "./components/MobileLunchBar";
import { Toaster } from "./components/ui/toaster";

// Import widgets (disable some for cleaner Gucci look)
import CookieConsent from "./components/CookieConsent";
import DynamicSEO from "./components/DynamicSEO";

// Inner page wrapper with Gucci styling - includes padding for fixed nav
const GucciPageWrapper = ({ children }) => {
  return (
    <GucciPageLayout>
      <div className="pt-[88px] md:pt-[100px]">
        {children}
      </div>
      <GucciFooter />
    </GucciPageLayout>
  );
};

// Layout wrapper that includes navigation and page transitions
const AppContent = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isAdminPage = location.pathname.startsWith('/admin');
  const isClassicPage = location.pathname === '/classic';
  const isPrintPage = location.pathname === '/lounaslista-tulosta';
  
  // Show TopBar and MainNavigation on all pages except admin
  const showGlobalNav = !isAdminPage && !isClassicPage && !isPrintPage;
  
  return (
    <PageTransitionProvider>
      {/* Site-wide maintenance notice (auto-hides after 10.07.2026) */}
      {!isAdminPage && !isPrintPage && <MaintenanceNotice />}
      {/* Global Navigation - shows on all pages except admin */}
      {showGlobalNav && !isHomePage && (
        <>
          <TopBar />
          <MainNavigation />
        </>
      )}
      
      <Routes>
        <Route path="/" element={<GucciSunsariHome />} />
        <Route path="/classic" element={<HorizontalSlideRestaurant />} />
        <Route path="/menu" element={<GucciPageWrapper><EnhancedMenuPage /></GucciPageWrapper>} />
        <Route path="/lunch-menu" element={<GucciPageWrapper><NordicLunchMenu /></GucciPageWrapper>} />
        <Route path="/offers" element={<GucciPageWrapper><OffersPage /></GucciPageWrapper>} />
        <Route path="/reservations" element={<GucciPageWrapper><StunningReservationPage /></GucciPageWrapper>} />
        <Route path="/table-booking" element={<GucciPageWrapper><InteractiveTableBooking /></GucciPageWrapper>} />
        <Route path="/combine-tables" element={<GucciPageWrapper><CombineTableBooking /></GucciPageWrapper>} />
        <Route path="/gift-cards" element={<GucciPageWrapper><GiftCardPage /></GucciPageWrapper>} />
        <Route path="/about" element={<GucciPageWrapper><AboutPage /></GucciPageWrapper>} />
        <Route path="/contact" element={<GucciPageWrapper><ContactPage /></GucciPageWrapper>} />
        <Route path="/catering" element={<GucciPageWrapper><CateringPage /></GucciPageWrapper>} />
        <Route path="/order" element={<GucciPageWrapper><OrderPage /></GucciPageWrapper>} />
        <Route path="/order-tracking/:orderId" element={<GucciPageWrapper><OrderTracking /></GucciPageWrapper>} />
        <Route path="/order-lookup" element={<GucciPageWrapper><OrderLookup /></GucciPageWrapper>} />
        <Route path="/my-orders" element={<GucciPageWrapper><CustomerPage /></GucciPageWrapper>} />
        <Route path="/ehdota" element={<GucciPageWrapper><SuggestDishPage /></GucciPageWrapper>} />
        <Route path="/suggest" element={<GucciPageWrapper><SuggestDishPage /></GucciPageWrapper>} />
        <Route path="/lounaslista-tulosta" element={<PrintableLunchMenu />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/customer-dashboard" element={<GucciPageWrapper><CustomerPage /></GucciPageWrapper>} />
      </Routes>

      {/* Mobile sticky quick-actions bar (phones only) */}
      {!isAdminPage && !isPrintPage && <MobileLunchBar />}
    </PageTransitionProvider>
  );
};

function App() {
  // Initialize AOS animations
  React.useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
      easing: 'ease-out-cubic'
    });
  }, []);

  return (
    <HelmetProvider>
      <LanguageProvider>
        <div className="App">
          {/* Dynamic SEO - Updates meta tags based on language */}
          <DynamicSEO />
          
          <BrowserRouter>
            {/* Main App Content with Page Transitions */}
            <AppContent />
            
            {/* Cookie Consent Banner - GDPR Compliance */}
            <CookieConsent />
            
            <Toaster />
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
